/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { createSlice } from '@reduxjs/toolkit';
import { apiSlice } from '../apiSlice';
import { TEMP_STORAGE_KEYS } from '../../constants/main';
import { clearTempStorageKeys } from '../../utils/auth/clearTempStorageKeys';
import { decodeJWT } from '../../utils/auth/decodeJWT';

const getStoredUser = () => {
  const storedEntry = window.localStorage.getItem(TEMP_STORAGE_KEYS.USER);
  return storedEntry && JSON.parse(storedEntry);
};

const decodedToken = window.localStorage.getItem(TEMP_STORAGE_KEYS.TOKEN) && decodeJWT(window.localStorage.getItem(TEMP_STORAGE_KEYS.TOKEN));

const initialState = {
  // Auth
  token: window.localStorage.getItem(TEMP_STORAGE_KEYS.TOKEN),
  refreshToken: window.localStorage.getItem(TEMP_STORAGE_KEYS.REFRESH_TOKEN),
  organizationId: window.localStorage.getItem(TEMP_STORAGE_KEYS.ORGANIZATION_ID),
  authorities: decodedToken && decodedToken.authorities,
  currentUser: getStoredUser(),

  // Reset password
  forgotUserMail: '',
  newPassword: '',
  resetPasswordCode: '',
  isResettingPassword: false,
  resetPasswordError: null,

  // Fetch current user
  isFetchingCurrentUser: false,
  fetchCurrentUserError: null,

  // Login user
  isLoggingInUser: false,
  loginUserError: null,
  isOidcAuthorizationCompletionInProgress: false,

  // Refresh token
  isRefreshingToken: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    userLoginStart: state => ({
      ...state,
      currentUser: null,
      isLoggingInUser: true,
      loginUserError: null
    }),

    userLoginFail: (state, action) => ({
      ...state,
      isLoggingInUser: false,
      loginUserError: action.payload.error
    }),

    userLoginComplete: (state, action) => {
      const decodedToken = decodeJWT(action.payload.token);
      const user = {
        ...decodedToken,
        ...action.payload.user
      };

      window.localStorage.setItem(TEMP_STORAGE_KEYS.USER, JSON.stringify(user));
      window.localStorage.setItem(TEMP_STORAGE_KEYS.TOKEN, `${action.payload.token}`);
      window.localStorage.setItem(TEMP_STORAGE_KEYS.REFRESH_TOKEN, `${action.payload.refreshToken}`);

      return {
        ...state,
        currentUser: user,
        isLoggingInUser: false,
        loginUserError: null,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken
      };
    },

    userFetchStart: state => ({
      ...state,
      isFetchingCurrentUser: true,
      fetchCurrentUserError: null
    }),

    userFetchFail: (state, action) => ({
      ...state,
      isFetchingCurrentUser: false,
      currentUser: null,
      fetchCurrentUserError: action.payload.error
    }),

    userFetchComplete: (state, action) => {
      window.localStorage.setItem(TEMP_STORAGE_KEYS.USER, JSON.stringify(action.payload));

      return {
        ...state,
        isFetchingCurrentUser: false,
        currentUser: action.payload,
        fetchCurrentUserError: null
      };
    },

    userForgotPasswordStart: state => ({
      ...state,
      isResettingPassword: true,
      resetPasswordError: null
    }),

    userForgotPasswordFail: (state, action) => ({
      ...state,
      isResettingPassword: false,
      forgotUserMail: '',
      resetPasswordError: action.payload.error
    }),

    userForgotPasswordComplete: (state, action) => ({
      ...state,
      isResettingPassword: false,
      forgotUserMail: action.payload,
      resetPasswordError: null
    }),

    userResetPasswordStart: state => ({
      ...state,
      isResettingPassword: true,
      resetPasswordError: null
    }),

    userResetPasswordFail: (state, action) => ({
      ...state,
      isResettingPassword: false,
      newPassword: '',
      resetPasswordCode: '',
      resetPasswordError: action.payload.error
    }),

    userResetPasswordComplete: (state, action) => ({
      ...state,
      isResettingPassword: false,
      newPassword: action.payload,
      resetPasswordCode: '',
      resetPasswordError: null
    }),

    refreshTokenStart: state => ({
      ...state,
      isRefreshingToken: true
    }),

    refreshTokenFail: (state, action) => ({
      ...state,
      isRefreshingToken: false
    }),

    refreshTokenComplete: (state, action) => {
      window.localStorage.setItem(TEMP_STORAGE_KEYS.TOKEN, `${action.payload.token}`);
      window.localStorage.setItem(TEMP_STORAGE_KEYS.REFRESH_TOKEN, `${action.payload.refreshToken}`);
      return {
        ...state,
        isRefreshingToken: false,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken
      };
    },

    organizationChangeComplete: (state, action) => {
      window.localStorage.setItem(TEMP_STORAGE_KEYS.ORGANIZATION_ID, `${action.payload}`);
      return {
        ...state,
        organizationId: action.payload
      };
    },

    userLogoutComplete: (state, _action) => {
      clearTempStorageKeys();
      apiSlice.util.resetApiState(); // This is needed in case the user will not refresh the page.

      return {
        ...state,
        ...initialState,
        currentUser: null,
        organizationId: null,
        token: null,
        refreshToken: null
      };
    },

    updateIsOidcAuthorizationCompletionInProgress: (state, action) => ({
      ...state,
      isOidcAuthorizationCompletionInProgress: action.payload
    })
  }
});

export const authActions = authSlice.actions;

export default authSlice.reducer;
